import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/react";
import { Mic2, Headphones, Loader2 } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { PageLoader } from "@/components/auth/Guards";
import { SectionLabel } from "@/components/ui-nw/UserAvatar";
import { errorMessage } from "@/lib/api";
import { ONBOARD } from "@/constants/testIds";

const ROLES = [
  { id: "creator", icon: Mic2, title: "AI Creator", text: "Showcase finished AI music & videos.", testId: ONBOARD.roleCreator },
  { id: "fan", icon: Headphones, title: "Fan", text: "Listen, watch, follow & comment. Free.", testId: ONBOARD.roleFan },
];

export default function Onboarding() {
  const { loading, isSignedIn, onboarded, request, apply } = useProfile();
  const { user } = useUser();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "fan" ? "fan" : localStorage.getItem("nw_role") || "creator");
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setDisplayName((v) => v || user.fullName || "");
    setUsername((v) => v || (user.primaryEmailAddress?.emailAddress || "").split("@")[0].replace(/[^a-zA-Z0-9_]/g, "").slice(0, 24));
  }, [user]);

  if (loading) return <PageLoader />;
  if (!isSignedIn) return <Navigate to="/sign-up" replace />;
  if (onboarded) return <Navigate to="/profile" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      apply(await request("post", "/me/onboard", { account_type: role, username, display_name: displayName }));
      localStorage.removeItem("nw_role");
      navigate("/profile");
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} data-testid={ONBOARD.page} className="mx-auto max-w-lg space-y-8 animate-rise">
      <div className="space-y-3">
        <SectionLabel>Step 2 of 2</SectionLabel>
        <h1 className="font-display text-4xl font-extrabold tracking-tight chrome-text">Choose your wave</h1>
        <p className="text-sm text-slate-400">How will you use NEW WAVE? You can upgrade a Fan account later.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {ROLES.map(({ id, icon: Icon, title, text, testId }) => (
          <button type="button" key={id} data-testid={testId} onClick={() => setRole(id)}
            className={`text-left rounded-2xl border p-4 transition-[border-color,background-color,transform] duration-200 active:scale-[0.98] ${role === id ? "border-nw-red bg-nw-red/10 shadow-[0_0_20px_rgba(255,30,39,0.25)]" : "border-white/10 bg-nw-surface/60 hover:border-white/25"}`}>
            <Icon className={`h-6 w-6 mb-3 ${role === id ? "text-nw-red" : "text-slate-400"}`} />
            <p className="font-display font-bold text-white">{title}</p>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">{text}</p>
          </button>
        ))}
      </div>
      <div className="space-y-4">
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Display name</span>
          <input data-testid={ONBOARD.displayName} required maxLength={50} value={displayName} onChange={(e) => setDisplayName(e.target.value)} className="nw-input" placeholder="Neon Prophet" />
        </label>
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest text-slate-400">Username</span>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">@</span>
            <input data-testid={ONBOARD.username} required pattern="[a-zA-Z0-9_]{3,24}" value={username} onChange={(e) => setUsername(e.target.value)} className="nw-input pl-8" placeholder="neonprophet" />
          </div>
          <span className="text-[11px] text-slate-500">3–24 letters, numbers or underscores.</span>
        </label>
      </div>
      {error && <p data-testid={ONBOARD.error} className="rounded-xl border border-nw-red/40 bg-nw-red/10 px-4 py-3 text-sm text-red-300">{error}</p>}
      <button data-testid={ONBOARD.submit} disabled={busy} className="btn-red w-full py-3.5 text-sm uppercase tracking-[0.2em] disabled:opacity-60">
        {busy ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : "Enter NEW WAVE"}
      </button>
    </form>
  );
}
